import { useLiveEvents } from "../lib/useLiveEvents";
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { VerifiedRating } from "../components/ReviewsWidget";
import { 
  Search, 
  FlaskConical, 
  Scan, 
  Activity, 
  Waves, 
  HeartPulse, 
  Wind, 
  Brain, 
  Ear, 
  Bone, 
  Sparkles, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  Home, 
  Building2,
  ChevronRight,
  Filter
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function DiagnosticsPage({ onSelectTest }) {
  const [searchParams] = useSearchParams();
  const [tests, setTests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [selectedSubCategory, setSelectedSubCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [homeCollectionOnly, setHomeCollectionOnly] = useState(false);
  const [detailTest, setDetailTest] = useState(null);

  useEffect(() => {
    const catParam = searchParams.get("cat");
    if (catParam) {
      setSelectedCategory(catParam);
    }
    fetchCategoriesAndTests();
  }, [searchParams]);
  useLiveEvents(["catalog"], () => fetchCategoriesAndTests());

  const fetchCategoriesAndTests = async () => {
    setLoading(true);
    try {
      axios.get(`${API}/diagnostic-hierarchy`).then((r) => setHierarchy(r.data)).catch(() => {});
      const [catsRes, testsRes] = await Promise.all([
        axios.get(`${API}/diagnostics/categories`),
        axios.get(`${API}/diagnostics`)
      ]);
      setCategories(catsRes.data || []);
      setTests(testsRes.data || []);

      const testParam = searchParams.get("test");
      if (testParam) {
        const found = testsRes.data.find(t => t.id === testParam);
        if (found) setDetailTest(found);
      }
    } catch (err) {
      console.error("Error fetching diagnostics:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredTests = tests.filter((t) => {
    const matchCat = selectedCategory === "All Categories" || t.category === selectedCategory;
    const matchSub = selectedSubCategory === "All" || (t.sub_category || "") === selectedSubCategory;
    const matchHome = !homeCollectionOnly || t.home_collection_available;
    const matchSearch = !searchTerm || (
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.code || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.tags || []).some((x) => x.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (t.alt_names || []).some((x) => x.toLowerCase().includes(searchTerm.toLowerCase())) ||
      t.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.sub_category && t.sub_category.toLowerCase().includes(searchTerm.toLowerCase())) ||
      t.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return (searchTerm ? true : matchCat && matchSub) && matchHome && matchSearch;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 space-y-10">
      
      {/* Header Banner */}
      <div className="bg-[#EBF4F1] p-8 sm:p-12 rounded-3xl border border-[#3B8271]/20 relative overflow-hidden">
        <div className="max-w-3xl space-y-3">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#0D3B2E] bg-white px-3 py-1 rounded-full border border-emerald-300 shadow-sm">
            NABL & AERB Aligned Clinical Diagnostic Lab
          </span>
          <h1 className="font-serif-luxury text-3xl sm:text-5xl text-[#0D3B2E] font-bold">
            Diagnostic Investigations Catalogue
          </h1>
          <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
            Browse over 50+ pathology, digital radiology, ultrasound, colour Doppler, cardiac and neuro-electrophysiology tests with transparent pricing & comprehensive preparation guidelines.
          </p>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative w-full md:flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search tests, scans & health checkups (name, code, keyword)…"
            data-testid="tests-search-input"
            className="w-full pl-10 pr-4 py-3 bg-white border-2 border-[#3B8271]/40 rounded-2xl text-sm text-gray-900 focus:outline-none focus:border-[#3B8271] shadow-md"
          />
        </div>

        {/* Home Sample Filter Toggle */}
        <label className="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer bg-white px-4 py-2.5 rounded-2xl border border-gray-200 shadow-sm">
          <input
            type="checkbox"
            checked={homeCollectionOnly}
            onChange={(e) => setHomeCollectionOnly(e.target.checked)}
            data-testid="home-collection-toggle"
            className="rounded text-[#0D3B2E] focus:ring-[#0D3B2E]"
          />
          <Home className="w-4 h-4 text-[#3B8271]" />
          <span>Home Sample Collection Available in Kolkata</span>
        </label>
      </div>

      {/* Category Icons Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <button
          onClick={() => { setSelectedCategory("All Categories"); setSelectedSubCategory("All"); }}
          data-testid="diag-cat-all"
          className={`p-3 rounded-2xl border text-center transition-all ${selectedCategory === "All Categories" ? "bg-[#0D3B2E] text-white shadow-md border-[#0D3B2E]" : "bg-white text-gray-700 hover:bg-gray-50 border-gray-200"}`}
        >
          <p className="text-xs font-bold">All {tests.length} Tests</p>
          <p className="text-[10px] opacity-80">Full Diagnostic Wings</p>
        </button>

        {categories.map((c) => (
          <button
            key={c.name}
            onClick={() => { setSelectedCategory(c.name); setSelectedSubCategory("All"); }}
            data-testid={`diag-cat-${c.name.toLowerCase().replace(/[\s']+/g, '-')}`}
            className={`p-3 rounded-2xl border text-center transition-all ${selectedCategory === c.name ? "bg-[#0D3B2E] text-white shadow-md border-[#0D3B2E]" : "bg-white text-gray-700 hover:bg-gray-50 border-gray-200"}`}
          >
            <p className="text-xs font-bold">{c.name}</p>
            <p className="text-[10px] opacity-80">{c.count} {c.count === 1 ? "Test" : "Tests"}{(() => { const n = (hierarchy.find((h) => h.name === c.name)?.subcategories || []).length; return n ? ` · ${n} ${n === 1 ? "group" : "groups"}` : ""; })()}</p>
          </button>
        ))}
      </div>

      {/* Subcategory chips (Step 2) */}
      {selectedCategory !== "All Categories" && (() => {
        const cat = hierarchy.find((h) => h.name === selectedCategory); const subs = cat?.subcategories || [];
        if (!subs.length) return null;
        return (
          <div className="bg-white rounded-2xl border border-gray-200 p-3 flex flex-wrap gap-2 items-center" data-testid="diag-subcategories">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mr-1">{selectedCategory} ›</span>
            <button onClick={() => setSelectedSubCategory("All")} data-testid="diag-sub-all" className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${selectedSubCategory === "All" ? "bg-[#0D3B2E] text-white border-[#0D3B2E]" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}>All {selectedCategory} ({cat.count})</button>
            {subs.map((sc) => <button key={sc.id} onClick={() => setSelectedSubCategory(sc.name)} data-testid={`diag-sub-${sc.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${selectedSubCategory === sc.name ? "bg-[#0D3B2E] text-white border-[#0D3B2E]" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}>{sc.name} <span className="opacity-70">({sc.count})</span></button>)}
          </div>
        );
      })()}

      {searchTerm && <p className="text-xs text-gray-500" data-testid="diag-search-summary">{filteredTests.length} result(s) for “{searchTerm}” across all categories</p>}
      {filteredTests.length === 0 && !loading && <p className="text-sm text-gray-500 bg-white border border-gray-200 rounded-2xl p-6 text-center" data-testid="diag-empty">No investigations match. Try another keyword or choose a different category.</p>}

      {/* Tests Catalog List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTests.map((test) => (
          <div
            key={test.id}
            data-testid={`test-item-card-${test.id}`}
            className="bg-white rounded-3xl border border-gray-200 shadow-sm hover:shadow-xl transition-all p-6 flex flex-col justify-between group"
          >
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-[#EBF4F1] text-[#0D3B2E]">
                  {test.category} {test.sub_category ? `• ${test.sub_category}` : ""}
                </span>
                {test.status === "available_by_appointment" ? (
                  <span className="text-[10px] text-amber-800 font-bold bg-amber-100 px-2 py-0.5 rounded-full">
                    By Appointment
                  </span>
                ) : (
                  <span className="text-[11px] text-emerald-800 font-bold bg-emerald-50 px-2 py-0.5 rounded-full">
                    {test.discount_pct}% OFF
                  </span>
                )}
              </div>

              <h3 className="font-serif-luxury text-lg font-bold text-gray-900 group-hover:text-[#0D3B2E] transition-colors line-clamp-2">
                {test.name}
              </h3>
              {test.rating_verified && <VerifiedRating rating={test.rating} verified count={test.reviews_count} testId={`test-rating-${test.id}`} />}

              <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">
                {test.description}
              </p>

              {/* Prep Note */}
              <div className="p-2.5 bg-[#FBF9F4] rounded-xl border border-gray-100 text-[11px] text-gray-600 space-y-0.5">
                <p><b>Prep:</b> {test.preparation_instructions}</p>
                <p className="text-gray-500">Sample: <b>{test.sample_type}</b> • Fasting: <b>{test.fasting_required ? "Yes" : "No"}</b></p>
              </div>
            </div>

            {/* Price & Action */}
            <div className="pt-4 mt-4 border-t border-gray-100 flex items-center justify-between">
              <div>
                <span className="text-xs text-gray-400 line-through mr-1.5">₹{test.mrp}</span>
                <span className="font-serif-luxury text-xl font-bold text-[#0D3B2E]">₹{test.price}</span>
              </div>

              <button
                onClick={() => onSelectTest(test)}
                data-testid={`book-test-action-${test.id}`}
                className="px-4 py-2 bg-[#0D3B2E] hover:bg-[#165644] text-white text-xs font-semibold rounded-xl transition-colors shadow"
              >
                Schedule Test
              </button>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
}
